/**
 * Device Utilities
 * Centralized functions for device management across all boards
 */

import { logger } from '../utils/logger';

interface DeviceWithBattery {
  sn: string;
  deviceName?: string;
  name?: string;
  nickname?: string;
  displayName?: string;
  batteryLevel?: number;
  hasBattery?: boolean;
  type?: string;
  groupId?: string;
  groupName?: string;
  status?: string;
  traits?: string[];
  [key: string]: any;
}

/**
 * Get all devices that have battery from IntelliSpace cache
 * ✅ 簡單直接：從 intellispace-devices-cache 讀取有 battery 欄位的設備
 * ✅ battery 欄位來自 QueryStatus API 的 system.battery
 * @returns Array of devices with battery
 */
export function getAllImportedDevicesWithBattery(): DeviceWithBattery[] {
  const cachedJson = localStorage.getItem('intellispace-devices-cache');
  if (!cachedJson) {
    logger.warn('🔋 [deviceUtils] No intellispace-devices-cache found');
    return [];
  }

  try {
    const cachedDevices = JSON.parse(cachedJson);
    const batteryDevices: DeviceWithBattery[] = [];
    const seenBaseSNs = new Set<string>();

    cachedDevices.forEach((device: any) => {
      // ✅ 直接檢查 battery 欄位（來自 QueryStatus 的 system.battery）
      if (device.battery !== undefined && typeof device.battery === 'number') {
        const baseSN = device.baseSN || device.deviceId?.split('#')[0] || device.deviceId;
        
        // 用 baseSN 去重（一個實體設備只算一次）
        if (!seenBaseSNs.has(baseSN)) {
          seenBaseSNs.add(baseSN);
          batteryDevices.push({
            sn: baseSN,
            deviceName: device.deviceName,
            name: device.deviceName,
            nickname: device.nickname,
            displayName: device.deviceName,
            batteryLevel: device.battery,
            battery: device.battery,
            hasBattery: true,
            type: device.deviceType,
            groupId: device.groupId,
            groupName: device.groupName,
            status: device.status
          });
        }
      }
    });

    logger.log(`🔋 [deviceUtils] Found ${batteryDevices.length} battery devices from cache`);
    return batteryDevices;
  } catch (error) {
    logger.error('Failed to parse intellispace-devices-cache', error);
    return [];
  }
}

/**
 * Get all imported devices from intelliSpace board (regardless of battery)
 * @returns Array of all imported devices
 */
export function getAllImportedDevices(): any[] {
  // Only read from intelliSpace board (legacy boards removed)
  const stored = localStorage.getItem('imported-devices-intelliSpace');
  if (!stored) {
    return [];
  }

  try {
    const devices = JSON.parse(stored);
    // Remove duplicates based on sn
    const uniqueDevices = devices.reduce((acc: any[], device: any) => {
      if (!acc.find((d: any) => d.sn === device.sn)) {
        acc.push(device);
      }
      return acc;
    }, [] as any[]);

    return uniqueDevices;
  } catch (error) {
    logger.error('Failed to parse devices from intelliSpace board', error);
    return [];
  }
}

/**
 * Get device display name (priority: nickname > displayName > name > deviceName > sn)
 * @param device Device object
 * @returns Display name
 */
export function getDeviceDisplayName(device: any): string {
  return device.nickname ||
         device.displayName ||
         device.name ||
         device.deviceName ||
         device.sn ||
         'Unknown Device';
}

/**
 * Board mode type (legacy boards removed - only intelliSpace remains)
 */
export type BoardMode = 'intelliSpace';

/**
 * Device type prefixes for intelliSpace board
 * This mapping is used to filter WebSocket updates by relevant device types
 * (Legacy boards removed - all device types now under intelliSpace)
 */
export const BOARD_DEVICE_PREFIXES: Record<BoardMode, string[]> = {
  intelliSpace: [                              // IntelliSpace: all device types
    'UT3M15', 'UTU500',                        // Power meters
    'UTU307', 'UTU308', 'UTU31E', 'UTU31G', 'UTU309', // TH sensors
    'UT2200', 'UTU3E0', 'HVAC',                // HVAC
    'UT2Z0S', 'AA2F01', 'UTU32A', 'UTU600', 'UT3M0F', // Lighting
    'UTU322',                                  // Presence sensors
    'UTU323'                                   // Door/Window sensors
  ]
};

/**
 * Check if a device belongs to a specific board mode
 * @param deviceSN Device serial number
 * @param mode Board mode (energy, climate, hvac, lighting)
 * @returns true if the device belongs to the specified board
 */
export function isDeviceForBoard(deviceSN: string, mode: BoardMode): boolean {
  if (!deviceSN || !mode) return false;

  const prefixes = BOARD_DEVICE_PREFIXES[mode];
  if (!prefixes) return false;

  // Check if device SN starts with any of the board's prefixes
  return prefixes.some(prefix => deviceSN.startsWith(prefix));
}

/**
 * Get the board mode for a given device
 * @param deviceSN Device serial number
 * @returns The board mode the device belongs to, or null if not found
 */
export function getBoardModeForDevice(deviceSN: string): BoardMode | null {
  if (!deviceSN) return null;

  for (const [mode, prefixes] of Object.entries(BOARD_DEVICE_PREFIXES)) {
    if (prefixes.some(prefix => deviceSN.startsWith(prefix))) {
      return mode as BoardMode;
    }
  }

  return null;
}

/**
 * Get all imported device SNs for a specific board
 * @param board Board name (energy, climate, hvac, lighting)
 * @returns Array of device SNs imported for that board
 */
export function getImportedDeviceSNsForBoard(board: BoardMode): string[] {
  const stored = localStorage.getItem(`imported-devices-${board}`);
  if (!stored) return [];

  try {
    const devices = JSON.parse(stored);
    return devices.map((d: any) => d.sn);
  } catch {
    return [];
  }
}

/**
 * Get device category (priority: user preference > auto detection)
 * @param deviceSN Device serial number
 * @returns The board mode the device belongs to, or null if not found
 */
export async function getDeviceCategory(deviceSN: string): Promise<BoardMode | null> {
  if (!deviceSN) return null;

  // 1. Try to get from user preferences (deviceCategories)
  try {
    const { getUserPreferences } = await import('../services/userPreferencesApi');
    const preferences = await getUserPreferences();
    const userCategory = preferences.deviceCategories?.[deviceSN];
    if (userCategory) {
      return userCategory as BoardMode;
    }
  } catch (error) {
    // If failed to get preferences, fall back to auto detection
    logger.warn('Failed to get device category from preferences, using auto detection:', error);
  }

  // 2. Fall back to auto detection
  return getBoardModeForDevice(deviceSN);
}
